/**
 * Procedural cube icons in Geometry Dash style: a square in the primary colour, a design in
 * the secondary colour and thick dark outlines. Every ICONS id has a painter here.
 * Cubes whose primary colour is nearly black would vanish on the game's dark backgrounds,
 * so they get a light outer outline and glow in their secondary colour instead.
 *
 * Shared by the shell (previews) and the game engine (players and ghosts, every frame), so
 * this module is pure drawing: no state, and every shape is precomputed once.
 * Painters draw in a local unit space: the cube spans [-1, 1] on both axes, y points down.
 * Line widths are in the same units, so icons scale cleanly from ~20 px to 200 px.
 */
import { COLORS, type ColorId, type IconId, type PlayerSkin } from '@cube-rush/shared';

/** Both canvas flavours provide everything the painters use. */
export type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** The part of a skin that defines the cube's look (the trail is drawn by the engine). */
export type CubeLook = Pick<PlayerSkin, 'icon' | 'primary' | 'secondary'>;

export interface CubeIconOptions {
  /** Rotation around the centre, radians. */
  rotation?: number;
  /** Opacity 0..1, multiplied with the context's current globalAlpha (ghosts). */
  alpha?: number;
  /** Neon glow around the cube in its accent colour (see cubeAccent). */
  glow?: boolean;
}

interface Palette {
  primary: string;
  secondary: string;
}

type Painter = (ctx: Ctx2D, palette: Palette) => void;

/** Outline colour: near-black reads better than pure black on dark-purple backgrounds. */
const INK = '#0a0614';
/** Outer outline of nearly black cubes. */
const LIGHT_INK = '#efe8ff';
const SHINE = 'rgba(255, 255, 255, 0.85)';
/** Outer outline width in units (1 unit = half the cube size). */
const OUTLINE = 0.13;
/** Outline width of inner details. */
const DETAIL = 0.09;
const INNER_EDGE = 1 - OUTLINE / 2;

const HEX: Readonly<Record<string, string>> = Object.fromEntries(COLORS.map((c) => [c.id, c.hex]));
/** Palette colours too dark to be seen on a dark background (computed once). */
const TOO_DARK: ReadonlySet<string> = new Set(
  COLORS.filter((c) => relativeLuminance(c.hex) < 0.03).map((c) => c.id),
);
/** The same colours as CSS hex (painters get colours, not ids): dark details need a rim there. */
const DARK_HEX: ReadonlySet<string> = new Set(
  COLORS.filter((c) => TOO_DARK.has(c.id)).map((c) => c.hex),
);

/** CSS colour of a palette entry (white for unknown ids from a newer server). */
export function skinColor(id: ColorId): string {
  return HEX[id] ?? '#ffffff';
}

/**
 * The colour a cube is recognised by on dark backgrounds: its primary colour, or the
 * secondary one when the primary is nearly black. Use it for glows, trails and highlights.
 */
export function cubeAccent(look: Pick<CubeLook, 'primary' | 'secondary'>): string {
  return skinColor(TOO_DARK.has(look.primary) ? look.secondary : look.primary);
}

/** WCAG relative luminance of a `#rrggbb` colour, 0 (black) .. 1 (white). */
function relativeLuminance(hex: string): number {
  const value = Number.parseInt(hex.slice(1), 16);
  const channel = (shift: number) => {
    const c = ((value >> shift) & 255) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
}

/**
 * Draws a cube centred at (cx, cy) with edge length `size` in the context's current units.
 * Restores the context state afterwards.
 */
export function drawCubeIcon(
  ctx: Ctx2D,
  look: CubeLook,
  cx: number,
  cy: number,
  size: number,
  options: CubeIconOptions = {},
): void {
  const palette: Palette = {
    primary: skinColor(look.primary),
    secondary: skinColor(look.secondary),
  };
  // Unknown ids can only come from a newer server; draw something sensible instead of crashing.
  const paint = PAINTERS[look.icon] ?? PAINTERS.classic;

  ctx.save();
  ctx.translate(cx, cy);
  if (options.rotation) ctx.rotate(options.rotation);
  ctx.scale(size / 2, size / 2);
  if (options.alpha !== undefined) ctx.globalAlpha *= options.alpha;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  if (options.glow) {
    // shadowBlur ignores the transform, so convert the cube size to device pixels.
    const m = ctx.getTransform();
    ctx.shadowColor = cubeAccent(look);
    ctx.shadowBlur = Math.hypot(m.a, m.b) * 0.5;
  }
  ctx.fillStyle = palette.primary;
  ctx.fillRect(-1, -1, 2, 2);
  if (options.glow) {
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
  }

  paint(ctx, palette);

  ctx.lineWidth = OUTLINE;
  ctx.strokeStyle = TOO_DARK.has(look.primary) ? LIGHT_INK : INK;
  ctx.strokeRect(-INNER_EDGE, -INNER_EDGE, INNER_EDGE * 2, INNER_EDGE * 2);
  ctx.restore();
}

// ---- path helpers ----

function polygon(ctx: Ctx2D, points: readonly number[]): void {
  ctx.beginPath();
  ctx.moveTo(points[0]!, points[1]!);
  for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i]!, points[i + 1]!);
  ctx.closePath();
}

function rectPath(ctx: Ctx2D, x: number, y: number, w: number, h: number): void {
  ctx.beginPath();
  ctx.rect(x, y, w, h);
}

function circlePath(ctx: Ctx2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
}

/** Rounded rectangle via arcTo (ctx.roundRect is missing on older iOS). */
function roundRectPath(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Closed outline of quadratic curves: [x0, y0, cx1, cy1, x1, y1, cx2, cy2, x2, y2, ...]. */
function curvePath(ctx: Ctx2D, points: readonly number[]): void {
  ctx.beginPath();
  ctx.moveTo(points[0]!, points[1]!);
  for (let i = 2; i < points.length; i += 4) {
    ctx.quadraticCurveTo(points[i]!, points[i + 1]!, points[i + 2]!, points[i + 3]!);
  }
  ctx.closePath();
}

/** Fills the current path and outlines it. */
function fillOutlined(ctx: Ctx2D, fill: string, width = DETAIL): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.strokeStyle = INK;
  ctx.stroke();
}

function fillPath(ctx: Ctx2D, fill: string): void {
  ctx.fillStyle = fill;
  ctx.fill();
}

function strokePath(ctx: Ctx2D, width: number, color = INK): void {
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.stroke();
}

/** Mirrors x coordinates of a flat [x0, y0, x1, y1, ...] list. */
function mirrorX(points: readonly number[]): number[] {
  return points.map((v, i) => (i % 2 === 0 ? -v : v));
}

/** Points of a 5-pointed star with outer radius 1 (computed once). */
const STAR_POINTS: readonly number[] = (() => {
  const points: number[] = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? 1 : 0.42;
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    points.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
  }
  return points;
})();

// ---- painters (one per ICONS id) ----

const BROW_LEFT = [-0.82, -0.66, -0.1, -0.36, -0.15, -0.2, -0.86, -0.5];
const BROW_RIGHT = mirrorX(BROW_LEFT);
const CAT_EAR_LEFT = [-0.87, -0.87, -0.3, -0.87, -0.87, -0.3];
const CAT_EAR_RIGHT = mirrorX(CAT_EAR_LEFT);
const NINJA_EYE_LEFT = [-0.66, -0.3, -0.14, -0.22, -0.2, -0.06, -0.66, -0.14];
const NINJA_EYE_RIGHT = mirrorX(NINJA_EYE_LEFT);
const COOL_LENS_LEFT = [-0.82, -0.44, -0.1, -0.44, -0.16, -0.02, -0.72, -0.02];
const COOL_LENS_RIGHT = mirrorX(COOL_LENS_LEFT);
const BOLT = [0.18, -0.86, -0.52, 0.12, -0.06, 0.12, -0.22, 0.86, 0.52, -0.16, 0.06, -0.16];
const CROSS_ARM = 0.21;
const CROSS_LENGTH = 0.95;
const CROSS = [
  CROSS_ARM,
  -CROSS_LENGTH,
  CROSS_ARM,
  -CROSS_ARM,
  CROSS_LENGTH,
  -CROSS_ARM,
  CROSS_LENGTH,
  CROSS_ARM,
  CROSS_ARM,
  CROSS_ARM,
  CROSS_ARM,
  CROSS_LENGTH,
  -CROSS_ARM,
  CROSS_LENGTH,
  -CROSS_ARM,
  CROSS_ARM,
  -CROSS_LENGTH,
  CROSS_ARM,
  -CROSS_LENGTH,
  -CROSS_ARM,
  -CROSS_ARM,
  -CROSS_ARM,
  -CROSS_ARM,
  -CROSS_LENGTH,
];
// Horns, eyes, fire, muzzle, nose and mouth below are curvePath lists; the rest are polygons.
const DRAGON_HORN_LEFT = [-0.26, -0.48, -0.58, -0.54, -0.72, -0.84, -0.94, -0.56, -0.8, -0.34];
const DRAGON_HORN_RIGHT = mirrorX(DRAGON_HORN_LEFT);
const DRAGON_EYE_LEFT = [-0.8, -0.26, -0.5, -0.46, -0.14, -0.08, -0.48, 0.2, -0.8, -0.26];
const DRAGON_EYE_RIGHT = mirrorX(DRAGON_EYE_LEFT);
/** The heavy lid over the top of a dragon's eye (clipped to the eye). */
const DRAGON_LID_LEFT = [-0.9, -0.6, 0, -0.6, 0, -0.14, -0.9, -0.34];
const DRAGON_LID_RIGHT = mirrorX(DRAGON_LID_LEFT);
/** Fangs under a crooked grin that breathes fire on the right (so they are not mirrored). */
const DRAGON_FANG_LEFT = [-0.42, 0.53, -0.24, 0.6, -0.35, 0.8];
const DRAGON_FANG_RIGHT = [0.28, 0.6, 0.12, 0.63, 0.22, 0.82];
/** Fire from the corner of the mouth and its white-hot core. */
const DRAGON_FLAME = [
  0.42, 0.52, 0.46, 0.32, 0.62, 0.22, 0.58, 0.34, 0.64, 0.42, 0.7, 0.3, 0.84, 0.26, 0.76, 0.4, 0.78,
  0.5, 0.8, 0.56, 0.86, 0.6, 0.76, 0.72, 0.6, 0.7, 0.46, 0.66, 0.42, 0.52,
];
const DRAGON_FLAME_CORE = [
  0.52, 0.56, 0.56, 0.44, 0.66, 0.5, 0.64, 0.58, 0.74, 0.62, 0.62, 0.68, 0.52, 0.56,
];
/** Dark sky between a wolf's ears: they stand out against it even on small cubes. */
const WOLF_NOTCH = [-0.715, -1, 0.715, -1, 0.24, -0.5, 0, -0.56, -0.24, -0.5];
const WOLF_EAR_LEFT = [-0.86, -0.36, -0.62, -0.9, -0.24, -0.5];
const WOLF_EAR_RIGHT = mirrorX(WOLF_EAR_LEFT);
const WOLF_INNER_EAR_LEFT = [-0.76, -0.42, -0.62, -0.78, -0.39, -0.53];
const WOLF_INNER_EAR_RIGHT = mirrorX(WOLF_INNER_EAR_LEFT);
const WOLF_CHEEK_LEFT = [-0.34, 0.04, -0.88, 0.06, -0.66, 0.26, -0.9, 0.44, -0.4, 0.58];
const WOLF_CHEEK_RIGHT = mirrorX(WOLF_CHEEK_LEFT);
/** The light muzzle: narrow between the eyes, wide at the jaw. */
const WOLF_MUZZLE = [
  -0.1, -0.32, 0, -0.32, 0.1, -0.32, 0.14, 0.02, 0.34, 0.18, 0.5, 0.34, 0.42, 0.62, 0.3, 0.9, 0,
  0.9, -0.3, 0.9, -0.42, 0.62, -0.5, 0.34, -0.34, 0.18, -0.14, 0.02, -0.1, -0.32,
];
const WOLF_EYE_LEFT = [-0.74, -0.26, -0.46, -0.44, -0.18, -0.1, -0.46, 0.1, -0.74, -0.26];
const WOLF_EYE_RIGHT = mirrorX(WOLF_EYE_LEFT);
/** Dark markings of a stern wolf: a stripe from the outer corner of the eye, a frowning brow. */
const WOLF_EYE_STRIPE_LEFT = [-0.66, -0.34, -0.88, -0.42, -0.74, -0.2];
const WOLF_EYE_STRIPE_RIGHT = mirrorX(WOLF_EYE_STRIPE_LEFT);
const WOLF_BROW_LEFT = [-0.46, -0.42, -0.14, -0.26, -0.2, -0.18, -0.44, -0.3];
const WOLF_BROW_RIGHT = mirrorX(WOLF_BROW_LEFT);
const WOLF_NOSE = [-0.24, 0.12, 0, 0.02, 0.24, 0.12, 0.2, 0.3, 0, 0.36, -0.2, 0.3, -0.24, 0.12];
const WOLF_MOUTH = [-0.32, 0.48, 0, 0.42, 0.32, 0.48, 0, 0.84, -0.32, 0.48];
const WOLF_FANG_LEFT = [-0.27, 0.48, -0.12, 0.46, -0.19, 0.58];
const WOLF_FANG_RIGHT = mirrorX(WOLF_FANG_LEFT);
/** A jack-o'-lantern: the cube is the pumpkin, the carved face glows in the second colour. */
const PUMPKIN_STEM = [-0.16, -1, -0.1, -0.76, 0.12, -0.74, 0.24, -1];
const PUMPKIN_EYE_LEFT = [-0.7, -0.1, -0.16, -0.1, -0.4, -0.54];
const PUMPKIN_EYE_RIGHT = mirrorX(PUMPKIN_EYE_LEFT);
const PUMPKIN_NOSE = [-0.13, 0.15, 0.13, 0.15, 0, -0.04];
/** The grin: two teeth hang from the upper lip, one stands on the lower lip. */
const PUMPKIN_MOUTH = [
  -0.8, 0.22, -0.4, 0.34, -0.4, 0.48, -0.18, 0.48, -0.18, 0.38, 0.18, 0.38, 0.18, 0.48, 0.4, 0.48,
  0.4, 0.34, 0.8, 0.22, 0.66, 0.54, 0.4, 0.74, 0.12, 0.8, 0.12, 0.64, -0.12, 0.64, -0.12, 0.8, -0.4,
  0.74, -0.66, 0.54,
];
/** A ghost: a round head, a body that hangs down in four scallops. */
const GHOST_SCALLOPS = [0.72, 0.36, 0, -0.36, -0.72];
/** A snowman: the second colour is the snow, the first one his scarf and carrot nose. */
const SNOWMAN_BODY = { x: 0, y: 0.46, r: 0.5 };
const SNOWMAN_HEAD = { x: 0, y: -0.24, r: 0.34 };
const SNOWMAN_NOSE = [0.02, -0.27, 0.44, -0.18, 0.02, -0.13];
const SNOWMAN_SCARF = [-0.4, 0, 0.4, 0, 0.38, 0.16, -0.38, 0.16];
const SNOWMAN_SCARF_END = [0.1, 0.12, 0.34, 0.12, 0.42, 0.5, 0.2, 0.52];
const SNOWMAN_HAT = [-0.26, -0.98, 0.26, -0.98, 0.26, -0.6, -0.26, -0.6];
const SNOWMAN_BRIM = [-0.44, -0.62, 0.44, -0.62, 0.44, -0.5, -0.44, -0.5];
/** Coal: eyes and buttons (x, y, radius). */
const SNOWMAN_COAL = [
  [-0.13, -0.32, 0.065],
  [0.13, -0.32, 0.065],
  [0, 0.4, 0.07],
  [0, 0.66, 0.07],
] as const;

const PAINTERS: Readonly<Record<IconId, Painter>> = {
  classic(ctx, { primary, secondary }) {
    rectPath(ctx, -0.56, -0.56, 1.12, 1.12);
    fillOutlined(ctx, secondary);
    rectPath(ctx, -0.22, -0.22, 0.44, 0.44);
    fillOutlined(ctx, primary);
  },

  smile(ctx, { secondary }) {
    rectPath(ctx, -0.56, -0.5, 0.3, 0.4);
    fillPath(ctx, INK);
    rectPath(ctx, 0.26, -0.5, 0.3, 0.4);
    fillPath(ctx, INK);
    rectPath(ctx, -0.5, -0.44, 0.11, 0.11);
    fillPath(ctx, SHINE);
    rectPath(ctx, 0.32, -0.44, 0.11, 0.11);
    fillPath(ctx, SHINE);
    ctx.beginPath();
    ctx.moveTo(-0.6, 0.12);
    ctx.lineTo(0.6, 0.12);
    ctx.quadraticCurveTo(0, 0.95, -0.6, 0.12);
    fillOutlined(ctx, INK);
    ctx.beginPath();
    ctx.moveTo(-0.26, 0.5);
    ctx.quadraticCurveTo(0, 0.3, 0.26, 0.5);
    ctx.quadraticCurveTo(0, 0.66, -0.26, 0.5);
    fillPath(ctx, secondary);
  },

  cool(ctx, { secondary }) {
    polygon(ctx, COOL_LENS_LEFT);
    fillPath(ctx, INK);
    polygon(ctx, COOL_LENS_RIGHT);
    fillPath(ctx, INK);
    rectPath(ctx, -0.9, -0.48, 1.8, 0.12);
    fillPath(ctx, INK);
    polygon(ctx, [-0.62, -0.36, -0.46, -0.36, -0.62, -0.1, -0.7, -0.1]);
    fillPath(ctx, secondary);
    polygon(ctx, [0.28, -0.36, 0.44, -0.36, 0.28, -0.1, 0.2, -0.1]);
    fillPath(ctx, secondary);
    ctx.beginPath();
    ctx.moveTo(-0.34, 0.46);
    ctx.quadraticCurveTo(0.12, 0.62, 0.5, 0.28);
    strokePath(ctx, DETAIL * 1.5);
  },

  angry(ctx, { secondary }) {
    polygon(ctx, BROW_LEFT);
    fillPath(ctx, INK);
    polygon(ctx, BROW_RIGHT);
    fillPath(ctx, INK);
    rectPath(ctx, -0.64, -0.2, 0.42, 0.3);
    fillOutlined(ctx, secondary);
    rectPath(ctx, 0.22, -0.2, 0.42, 0.3);
    fillOutlined(ctx, secondary);
    rectPath(ctx, -0.42, -0.14, 0.16, 0.2);
    fillPath(ctx, INK);
    rectPath(ctx, 0.26, -0.14, 0.16, 0.2);
    fillPath(ctx, INK);
    rectPath(ctx, -0.52, 0.34, 1.04, 0.34);
    fillOutlined(ctx, INK);
    polygon(ctx, [-0.46, 0.38, -0.3, 0.38, -0.38, 0.54, 0.3, 0.38, 0.46, 0.38, 0.38, 0.54]);
    fillPath(ctx, secondary);
  },

  robot(ctx, { secondary }) {
    roundRectPath(ctx, -0.78, -0.62, 1.56, 0.64, 0.14);
    fillPath(ctx, INK);
    circlePath(ctx, -0.38, -0.3, 0.17);
    fillPath(ctx, secondary);
    circlePath(ctx, 0.38, -0.3, 0.17);
    fillPath(ctx, secondary);
    circlePath(ctx, -0.43, -0.35, 0.05);
    fillPath(ctx, SHINE);
    circlePath(ctx, 0.33, -0.35, 0.05);
    fillPath(ctx, SHINE);
    roundRectPath(ctx, -0.52, 0.24, 1.04, 0.4, 0.08);
    fillOutlined(ctx, secondary);
    ctx.beginPath();
    for (const x of [-0.26, 0, 0.26]) {
      ctx.moveTo(x, 0.28);
      ctx.lineTo(x, 0.6);
    }
    strokePath(ctx, DETAIL * 0.8);
    for (const [x, y] of [
      [-0.78, -0.8],
      [0.78, -0.8],
      [-0.78, 0.8],
      [0.78, 0.8],
    ] as const) {
      circlePath(ctx, x, y, 0.07);
      fillPath(ctx, INK);
    }
  },

  cat(ctx, { secondary }) {
    polygon(ctx, CAT_EAR_LEFT);
    fillOutlined(ctx, secondary);
    polygon(ctx, CAT_EAR_RIGHT);
    fillOutlined(ctx, secondary);
    circlePath(ctx, -0.38, -0.12, 0.19);
    fillPath(ctx, INK);
    circlePath(ctx, 0.38, -0.12, 0.19);
    fillPath(ctx, INK);
    rectPath(ctx, -0.41, -0.26, 0.06, 0.28);
    fillPath(ctx, secondary);
    rectPath(ctx, 0.35, -0.26, 0.06, 0.28);
    fillPath(ctx, secondary);
    polygon(ctx, [-0.13, 0.17, 0.13, 0.17, 0, 0.32]);
    fillOutlined(ctx, secondary, DETAIL * 0.7);
    ctx.beginPath();
    ctx.moveTo(-0.28, 0.42);
    ctx.quadraticCurveTo(-0.14, 0.56, 0, 0.4);
    ctx.quadraticCurveTo(0.14, 0.56, 0.28, 0.42);
    ctx.moveTo(-0.46, 0.28);
    ctx.lineTo(-0.86, 0.2);
    ctx.moveTo(-0.46, 0.4);
    ctx.lineTo(-0.86, 0.46);
    ctx.moveTo(0.46, 0.28);
    ctx.lineTo(0.86, 0.2);
    ctx.moveTo(0.46, 0.4);
    ctx.lineTo(0.86, 0.46);
    strokePath(ctx, DETAIL * 0.7);
  },

  star(ctx, { secondary }) {
    ctx.beginPath();
    for (let i = 0; i < STAR_POINTS.length; i += 2) {
      const x = STAR_POINTS[i]! * 0.76;
      const y = STAR_POINTS[i + 1]! * 0.76 + 0.05;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    fillOutlined(ctx, secondary);
  },

  diamond(ctx, { primary, secondary }) {
    polygon(ctx, [0, -0.78, 0.7, 0, 0, 0.78, -0.7, 0]);
    fillOutlined(ctx, secondary);
    polygon(ctx, [0, -0.36, 0.32, 0, 0, 0.36, -0.32, 0]);
    fillOutlined(ctx, primary, DETAIL * 0.8);
  },

  cross(ctx, { secondary }) {
    ctx.save();
    ctx.rotate(Math.PI / 4);
    polygon(ctx, CROSS);
    fillOutlined(ctx, secondary);
    ctx.restore();
  },

  target(ctx, { primary, secondary }) {
    circlePath(ctx, 0, 0, 0.74);
    fillOutlined(ctx, secondary);
    circlePath(ctx, 0, 0, 0.5);
    fillOutlined(ctx, primary);
    circlePath(ctx, 0, 0, 0.24);
    fillOutlined(ctx, secondary);
  },

  stripes(ctx, { secondary }) {
    ctx.save();
    rectPath(ctx, -1, -1, 2, 2);
    ctx.clip();
    ctx.rotate(-Math.PI / 4);
    for (let x = -1.3; x <= 1.3; x += 0.52) {
      rectPath(ctx, x, -1.5, 0.24, 3);
      fillOutlined(ctx, secondary, DETAIL * 0.7);
    }
    ctx.restore();
  },

  checker(ctx, { secondary }) {
    ctx.beginPath();
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        if ((row + col) % 2 === 1) ctx.rect(-1 + col * 0.5, -1 + row * 0.5, 0.5, 0.5);
      }
    }
    fillOutlined(ctx, secondary, DETAIL * 0.6);
  },

  alien(ctx, { secondary }) {
    ctx.beginPath();
    ctx.moveTo(0, -0.84);
    ctx.bezierCurveTo(0.8, -0.84, 0.88, 0.04, 0, 0.84);
    ctx.bezierCurveTo(-0.88, 0.04, -0.8, -0.84, 0, -0.84);
    fillOutlined(ctx, secondary);
    ctx.beginPath();
    ctx.ellipse(-0.3, -0.06, 0.26, 0.14, 0.55, 0, Math.PI * 2);
    fillPath(ctx, INK);
    ctx.beginPath();
    ctx.ellipse(0.3, -0.06, 0.26, 0.14, -0.55, 0, Math.PI * 2);
    fillPath(ctx, INK);
    circlePath(ctx, -0.36, -0.1, 0.05);
    fillPath(ctx, SHINE);
    circlePath(ctx, 0.24, -0.1, 0.05);
    fillPath(ctx, SHINE);
    ctx.beginPath();
    ctx.moveTo(-0.12, 0.4);
    ctx.lineTo(0.12, 0.4);
    strokePath(ctx, DETAIL);
  },

  heart(ctx, { secondary }) {
    ctx.beginPath();
    ctx.moveTo(0, 0.74);
    ctx.bezierCurveTo(-1.0, 0.04, -0.62, -0.8, 0, -0.3);
    ctx.bezierCurveTo(0.62, -0.8, 1.0, 0.04, 0, 0.74);
    fillOutlined(ctx, secondary);
    ctx.beginPath();
    ctx.ellipse(-0.36, -0.22, 0.12, 0.07, -0.7, 0, Math.PI * 2);
    fillPath(ctx, SHINE);
  },

  bolt(ctx, { secondary }) {
    polygon(ctx, BOLT);
    fillOutlined(ctx, secondary);
  },

  ninja(ctx, { secondary }) {
    rectPath(ctx, -1, -0.44, 2, 0.56);
    fillPath(ctx, INK);
    polygon(ctx, NINJA_EYE_LEFT);
    fillPath(ctx, secondary);
    polygon(ctx, NINJA_EYE_RIGHT);
    fillPath(ctx, secondary);
    rectPath(ctx, -1, -0.86, 2, 0.24);
    fillOutlined(ctx, secondary);
    polygon(ctx, [0.5, -0.62, 0.84, -0.3, 0.7, -0.22, 0.4, -0.58]);
    fillOutlined(ctx, secondary, DETAIL * 0.8);
  },

  dragon(ctx, { secondary }) {
    curvePath(ctx, DRAGON_HORN_LEFT);
    fillOutlined(ctx, secondary);
    curvePath(ctx, DRAGON_HORN_RIGHT);
    fillOutlined(ctx, secondary);
    ctx.beginPath();
    for (const side of [-1, 1]) {
      ctx.moveTo(side * 0.6, -0.46);
      ctx.lineTo(side * 0.83, -0.5);
      ctx.moveTo(side * 0.68, -0.63);
      ctx.lineTo(side * 0.85, -0.65);
    }
    strokePath(ctx, DETAIL * 0.8);
    for (const [eye, lid, side] of [
      [DRAGON_EYE_LEFT, DRAGON_LID_LEFT, -1],
      [DRAGON_EYE_RIGHT, DRAGON_LID_RIGHT, 1],
    ] as const) {
      curvePath(ctx, eye);
      fillPath(ctx, secondary);
      ctx.save();
      ctx.clip();
      polygon(ctx, lid);
      fillPath(ctx, INK);
      ctx.restore();
      curvePath(ctx, eye);
      strokePath(ctx, DETAIL);
      ctx.beginPath();
      ctx.ellipse(side * 0.47, -0.09, 0.055, 0.13, 0, 0, Math.PI * 2);
      fillPath(ctx, INK);
      ctx.beginPath();
      ctx.ellipse(side * 0.17, 0.22, 0.1, 0.05, side * 0.55, 0, Math.PI * 2);
      fillPath(ctx, INK);
    }
    ctx.beginPath();
    ctx.moveTo(-0.66, 0.38);
    ctx.quadraticCurveTo(-0.12, 0.8, 0.46, 0.52);
    strokePath(ctx, DETAIL * 1.4);
    polygon(ctx, DRAGON_FANG_LEFT);
    fillOutlined(ctx, secondary, DETAIL * 0.8);
    polygon(ctx, DRAGON_FANG_RIGHT);
    fillOutlined(ctx, secondary, DETAIL * 0.8);
    curvePath(ctx, DRAGON_FLAME);
    fillOutlined(ctx, secondary, DETAIL * 0.9);
    curvePath(ctx, DRAGON_FLAME_CORE);
    fillPath(ctx, SHINE);
  },

  wolf(ctx, { primary, secondary }) {
    polygon(ctx, WOLF_NOTCH);
    fillPath(ctx, INK);
    polygon(ctx, WOLF_EAR_LEFT);
    fillOutlined(ctx, primary);
    polygon(ctx, WOLF_EAR_RIGHT);
    fillOutlined(ctx, primary);
    polygon(ctx, WOLF_INNER_EAR_LEFT);
    fillPath(ctx, secondary);
    polygon(ctx, WOLF_INNER_EAR_RIGHT);
    fillPath(ctx, secondary);
    // Cheek fur and muzzle are one light shape: all outlines first, twice as wide (the fills
    // cover their inner halves), then all fills.
    polygon(ctx, WOLF_CHEEK_LEFT);
    strokePath(ctx, DETAIL * 2);
    polygon(ctx, WOLF_CHEEK_RIGHT);
    strokePath(ctx, DETAIL * 2);
    curvePath(ctx, WOLF_MUZZLE);
    strokePath(ctx, DETAIL * 2);
    polygon(ctx, WOLF_CHEEK_LEFT);
    fillPath(ctx, secondary);
    polygon(ctx, WOLF_CHEEK_RIGHT);
    fillPath(ctx, secondary);
    curvePath(ctx, WOLF_MUZZLE);
    fillPath(ctx, secondary);
    for (const [eye, stripe, brow, x] of [
      [WOLF_EYE_LEFT, WOLF_EYE_STRIPE_LEFT, WOLF_BROW_LEFT, -0.44],
      [WOLF_EYE_RIGHT, WOLF_EYE_STRIPE_RIGHT, WOLF_BROW_RIGHT, 0.44],
    ] as const) {
      curvePath(ctx, eye);
      fillOutlined(ctx, secondary);
      circlePath(ctx, x, -0.15, 0.09);
      fillPath(ctx, INK);
      circlePath(ctx, x - 0.035, -0.185, 0.03);
      fillPath(ctx, SHINE);
      polygon(ctx, stripe);
      fillPath(ctx, INK);
      polygon(ctx, brow);
      fillPath(ctx, INK);
    }
    curvePath(ctx, WOLF_NOSE);
    fillOutlined(ctx, INK, DETAIL * 0.6);
    ctx.beginPath();
    ctx.ellipse(-0.09, 0.12, 0.08, 0.04, -0.2, 0, Math.PI * 2);
    fillPath(ctx, SHINE);
    curvePath(ctx, WOLF_MOUTH);
    fillOutlined(ctx, INK, DETAIL * 0.6);
    polygon(ctx, WOLF_FANG_LEFT);
    fillPath(ctx, secondary);
    polygon(ctx, WOLF_FANG_RIGHT);
    fillPath(ctx, secondary);
  },

  pumpkin(ctx, { secondary }) {
    // The pumpkin's grooves fan out from the stem over the sides.
    ctx.beginPath();
    for (const side of [-1, 1]) {
      ctx.moveTo(side * 0.16, -0.84);
      ctx.quadraticCurveTo(side * 0.7, -0.78, side * 0.86, -0.26);
      ctx.moveTo(side * 0.88, 0.32);
      ctx.quadraticCurveTo(side * 0.84, 0.72, side * 0.62, 0.96);
    }
    strokePath(ctx, DETAIL * 0.7);
    polygon(ctx, PUMPKIN_STEM);
    fillOutlined(ctx, secondary);
    // The carved face glows from inside in the second colour.
    for (const eye of [PUMPKIN_EYE_LEFT, PUMPKIN_EYE_RIGHT]) {
      polygon(ctx, eye);
      fillOutlined(ctx, secondary);
    }
    polygon(ctx, PUMPKIN_NOSE);
    fillOutlined(ctx, secondary, DETAIL * 0.8);
    polygon(ctx, PUMPKIN_MOUTH);
    fillOutlined(ctx, secondary);
  },

  ghost(ctx, { secondary }) {
    ctx.beginPath();
    ctx.moveTo(-0.72, 0.6);
    ctx.lineTo(-0.72, -0.18);
    ctx.bezierCurveTo(-0.72, -1.02, 0.72, -1.02, 0.72, -0.18);
    ctx.lineTo(0.72, 0.6);
    for (let i = 1; i < GHOST_SCALLOPS.length; i++) {
      const from = GHOST_SCALLOPS[i - 1]!;
      const to = GHOST_SCALLOPS[i]!;
      ctx.quadraticCurveTo((from + to) / 2, 1.08, to, 0.6);
    }
    ctx.closePath();
    fillOutlined(ctx, secondary);
    // Big dark eyes and a round «Ууу!» mouth; on a nearly black ghost they get a light rim.
    const rim = DARK_HEX.has(secondary);
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(side * 0.3, -0.2, 0.15, 0.21, side * 0.12, 0, Math.PI * 2);
      fillPath(ctx, INK);
      if (rim) strokePath(ctx, DETAIL * 0.6, LIGHT_INK);
      circlePath(ctx, side * 0.3 - 0.05, -0.28, 0.05);
      fillPath(ctx, SHINE);
    }
    ctx.beginPath();
    ctx.ellipse(0, 0.26, 0.11, 0.15, 0, 0, Math.PI * 2);
    fillPath(ctx, INK);
    if (rim) strokePath(ctx, DETAIL * 0.6, LIGHT_INK);
  },

  snowman(ctx, { primary, secondary }) {
    // Stick arms behind the body.
    ctx.beginPath();
    for (const side of [-1, 1]) {
      ctx.moveTo(side * 0.36, 0.34);
      ctx.lineTo(side * 0.88, 0);
      ctx.moveTo(side * 0.7, 0.12);
      ctx.lineTo(side * 0.84, 0.3);
    }
    strokePath(ctx, DETAIL * 1.2);
    // Snow: the body, then the head over it.
    for (const { x, y, r } of [SNOWMAN_BODY, SNOWMAN_HEAD]) {
      circlePath(ctx, x, y, r);
      fillOutlined(ctx, secondary);
    }
    polygon(ctx, SNOWMAN_SCARF_END);
    fillOutlined(ctx, primary, DETAIL * 0.8);
    polygon(ctx, SNOWMAN_SCARF);
    fillOutlined(ctx, primary, DETAIL * 0.8);
    polygon(ctx, SNOWMAN_BRIM);
    fillPath(ctx, INK);
    polygon(ctx, SNOWMAN_HAT);
    fillPath(ctx, INK);
    rectPath(ctx, -0.26, -0.72, 0.52, 0.1);
    fillPath(ctx, primary);
    // Coal eyes and buttons (with a light rim in dark snow), a carrot nose.
    const rim = DARK_HEX.has(secondary);
    for (const [x, y, r] of SNOWMAN_COAL) {
      circlePath(ctx, x, y, r);
      fillPath(ctx, INK);
      if (rim) strokePath(ctx, DETAIL * 0.5, LIGHT_INK);
    }
    polygon(ctx, SNOWMAN_NOSE);
    fillOutlined(ctx, primary, DETAIL * 0.7);
  },
};
